import { useLocation } from "react-router-dom";
import { App, Table, Tag, Flex, Typography } from "antd";
import { useCallback, useMemo, useContext } from "react";

import PencilAltIcon from "@/icons/PencilAltIcon";
import LockCloseIcon from "@/icons/LockClosedIcon";
import CheckIcon from "@/icons/CheckIcon";
import ArrowTopRightIcon from "@/icons/ArrowTopRightIocn";

import { UserContext } from "@/context/UserContext";

import EmployeeProfile from "@/components/cards/EmployeeProfile";
import CustomLink from "@/components/ui/CustomLink";
import DownloadLink from "@/components/ui/DowloadLink";
import ImageWithPreview from "@/components/ui/ImageWithPreview";
import ContextMenu from "@/components/containers/ContextMenu";
import ExpandableText from "@/components/views/ObjectFieldValue/ExpandableText";

import { copyLinkToClipboard, formateISODateToStr, getIconByType } from "@/utils";
import routes from "@/routes";
import { useColumnConfig } from "@/hooks/useColumnConfig";

const LOCAL_STORAGE_KEY = "fileTableColumnParameters";

const calcSortOrder = (sortedInfo, field) => {
  if (!sortedInfo) return null;

  const sorterArray = Array.isArray(sortedInfo) ? sortedInfo : [sortedInfo];
  const col = sorterArray.find(
    ({ columnKey, field: sorterField }) =>
      columnKey === field || sorterField === field,
  );

  return col ? col.order : null;
};

const COLUMN_LABELS = {
  name: "Имя",
  type: "Тип",
  unit: "Рабочая группа",
  size: "Размер",
  created_at: "Дата добавления",
  author: "Автор",
  description: "Описание",
};

const formatFileSize = (size) => {
  const bytes = Number(size);

  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes === 0) return "0 Б";

  const units = ["Б", "КБ", "МБ", "ГБ", "ТБ"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;
  const fractionDigits = value >= 10 || unitIndex === 0 ? 0 : 1;

  return `${value.toLocaleString("ru-RU", {
    maximumFractionDigits: fractionDigits,
  })} ${units[unitIndex]}`;
};

// Таблица файлов
// Содержит список с возможностью сортировки
// Тут можно настроить колонки
const FilesTable = ({
  data,
  loading,
  pagination,
  isCompactCatalog,
  onChange,
  onEditItem,
  onDeleteItem,
  unit_uuid,
  show_unit = true,
  show_description = false,
  readOnly = false,
  resize = false,
  mode = "default", // "default" | "picker"
  onOpenFolder,
  onPickFile,
  rowSelection,
  rowClassName,
  sortedInfo,
  scroll = { x: "100%" },
}) => {
  const location = useLocation();
  const { message } = App.useApp();
  const userCtx = useContext(UserContext);
  const user = userCtx?.user || null;

  const isAdmins = location.pathname.startsWith("/administration");

  const getFileLink = useCallback(
    (item) => {
      const folderUuid = item.folder_uuid || "catalog";
      const pathname = item.folder_uuid
        ? unit_uuid
          ? routes.unitFolder(unit_uuid, folderUuid)
          : isAdmins
            ? routes.administration.folder(folderUuid)
            : routes.folder(folderUuid)
        : location.pathname;
      const url = new URL(pathname, window.location.origin);

      url.searchParams.set("file_uuid", item.uuid);

      return url.toString();
    },
    [isAdmins, location.pathname, unit_uuid],
  );

  const handleCopyFileLink = useCallback(
    async (item) => {
      const result = await copyLinkToClipboard(getFileLink(item));

      message.open({
        type: result.success ? "success" : "error",
        content: result.message,
      });
    },
    [getFileLink, message],
  );

  const baseColumns = useMemo(() => {
    const isPicker = mode === "picker";
    const currentSearchParams = new URLSearchParams(location.search);

    return [
      {
        title: <PencilAltIcon style={{ fontSize: 14, color: "#BBBBBB" }} />,
        dataIndex: "readOnly",
        key: "readOnly",
        width: 50,
        fixed: !isCompactCatalog ? "left" : false,
        align: "center",
        render: (readOnly) =>
          readOnly ? (
            <LockCloseIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <CheckIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ),
      },
      {
        title: COLUMN_LABELS.name,
        dataIndex: "name",
        key: "name",
        width: 300,
        sortOrder: calcSortOrder(sortedInfo, "name"),
        ellipsis: true,
        fixed: !isCompactCatalog ? "left" : false,
        sorter: { compare: (a, b) => a.name.localeCompare(b.name) },
        render: (name, record) => {
          const {
            type,
            extension,
            element_type,
            uuid,
            original_name,
            is_shortcut,
          } = record;

          const textNode = (
            <Typography.Text
              ellipsis={{
                tooltip: `${name}${extension ? `.${extension}` : ""}`,
              }}
            >
              {name}
              {extension ? `.${extension}` : ""}
            </Typography.Text>
          );

          if (isPicker) {
            if (element_type === "folder") {
              return (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenFolder?.(uuid)}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && onOpenFolder?.(uuid)
                  }
                  style={{
                    cursor: "pointer",
                    color: "var(--color-link, #1677ff)",
                  }}
                >
                  <Flex align="center" gap={5}>
                    {getIconByType(type, extension, element_type)}
                    <div style={{ minWidth: 0, flex: 1 }}>{textNode}</div>
                  </Flex>
                </span>
              );
            }
            return (
              <span
                role="button"
                tabIndex={0}
                title="Щёлкните, чтобы выбрать"
                onClick={() => onPickFile?.(record)}
                onKeyDown={(e) => e.key === "Enter" && onPickFile?.(record)}
                style={{ cursor: "default" }}
              >
                <Flex align="center" gap={5}>
                  <div
                    style={{
                      position: "relative",
                      height: 22,
                    }}
                  >
                    {is_shortcut && (
                      <ArrowTopRightIcon
                        style={{
                          position: "absolute",
                          bottom: -1,
                          left: 0,
                          fontSize: "12px",
                          color: "var(--color-secondary)",
                          background: "#fff",
                          borderTopRightRadius: 3,
                        }}
                      />
                    )}
                    {getIconByType(type, extension, element_type)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>{textNode}</div>
                </Flex>
              </span>
            );
          }
          return (
            <>
              {element_type === "folder" ? (
                <CustomLink
                  to={
                    unit_uuid
                      ? routes.unitFolder(unit_uuid, uuid)
                      : `${
                          isAdmins
                            ? routes.administration.folder(uuid)
                            : routes.folder(uuid)
                        }?${currentSearchParams.toString()}`
                  }
                >
                  <Flex align="center" gap={5}>
                    {getIconByType(type, extension, element_type)}
                    <div style={{ minWidth: 0, flex: 1 }}>{textNode}</div>
                  </Flex>
                </CustomLink>
              ) : type === "image" ? (
                <ImageWithPreview
                  uuid={uuid}
                  original_name={original_name}
                  name={name}
                  extension={extension}
                  is_shortcut={is_shortcut}
                />
              ) : (
                <DownloadLink
                  href={`/api/file/download?uuid=${uuid}`}
                  original_name={original_name}
                >
                  <div style={{ position: "relative", height: 22 }}>
                    {is_shortcut && (
                      <ArrowTopRightIcon
                        style={{
                          position: "absolute",
                          bottom: -1,
                          left: 0,
                          fontSize: "12px",
                          color: "var(--color-secondary)",
                          background: "#fff",
                          borderRadius: 4,
                        }}
                      />
                    )}
                    {getIconByType(type, extension, element_type)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>{textNode}</div>
                </DownloadLink>
              )}
            </>
          );
        },
      },
      {
        title: COLUMN_LABELS.type,
        width: 100,
        dataIndex: "type",
        key: "type",
        render: (type, { element_type }) =>
          element_type !== "folder" && <Tag>{type}</Tag>,
      },
      {
        title: COLUMN_LABELS.size,
        dataIndex: "size",
        key: "size",
        width: 110,
        render: (size, { element_type }) =>
          element_type !== "folder" ? formatFileSize(size) : "",
      },
      show_unit
        ? {
            title: COLUMN_LABELS.unit,
            dataIndex: "unit",
            key: "unit",
            ellipsis: true,
            width: 170,
            render: (_, item) => item.unit?.name,
          }
        : null,
      {
        title: COLUMN_LABELS.created_at,
        dataIndex: "created_at",
        key: "created_at",
        width: 160,
        sortOrder: calcSortOrder(sortedInfo, "created_at"),
        sorter: {
          compare: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        render: (created_at) => formateISODateToStr(created_at),
      },
      {
        title: COLUMN_LABELS.author,
        dataIndex: "author",
        key: "author",
        ellipsis: true,
        width: 250,
        render: (author) => <EmployeeProfile noAvatar {...author} />,
      },
      show_description
        ? {
            title: COLUMN_LABELS.description,
            dataIndex: "description",
            key: "description",
            width: 250,
            render: (description) => (
              <ExpandableText value={description || ""} line={2} />
            ),
          }
        : null,
      !readOnly && !isPicker
        ? {
            title: "",
            align: "center",
            width: 43,
            fixed: "right",
            key: "actions",
            render: (_, item) =>
              item.element_type !== "folder" &&
              !item.readOnly && (
                <ContextMenu
                  vertical
                  onCopyLink={() => handleCopyFileLink(item)}
                  onEdit={() => onEditItem(item)}
                  onDelete={() => onDeleteItem(item)}
                />
              ),
          }
        : null,
    ].filter(Boolean);
  }, [
    mode,
    location.search,
    isAdmins,
    handleCopyFileLink,
    onDeleteItem,
    onEditItem,
    readOnly,
    show_description,
    show_unit,
    unit_uuid,
    onOpenFolder,
    onPickFile,
    isCompactCatalog,
    sortedInfo,
  ]);

  const { columns, components } = useColumnConfig(
    baseColumns,
    user?.user_uuid,
    LOCAL_STORAGE_KEY,
    {
      resize,
      columnLabels: COLUMN_LABELS,
    },
  );

  return (
    <Table
      onRow={() => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      })}
      dataSource={data}
      columns={columns}
      rowKey="uuid"
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      components={resize ? components : undefined}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      tableLayout="fixed"
      bordered
      rowClassName={rowClassName}
      rowSelection={rowSelection}
    />
  );
};

export default FilesTable;
